import { Loader } from '../Widgets';
import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { AppSettings, Employee } from 'service/Models';
import ServerGateway from 'service/ServerGateway';
import Webcam from "react-webcam";
import toast, { Toaster } from 'react-hot-toast';
import SettingsService from 'service/SettingsService';

import * as faceapi from '@vladmandic/face-api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faFaceSmile, faLocation, faLocationPin, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router';
const { DrawBox } = faceapi.draw;

export const RegisterFace = () => {
    const user = useSelector((state: RootState) => state.appdata);
    const [employee, setEmployee] = useState<Employee>();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [captureVideo, setCaptureVideo] = useState(false);
    const [faceRecognitionStatus, setFaceRecognitionStatus] = useState<string>("Not Started");
    const [isFaceDetected, setIsFaceDetected] = useState<boolean>(false);
    const [faceDescriptors, setFaceDescriptors] = useState<(Float32Array | null)[]>([null, null, null]);
    const [currentFaceDescriptor, setCurrentFaceDescriptor] = useState<Float32Array>();
    const [currentSlot, setCurrentSlot] = useState(0);
    const navigate = useNavigate();

    const webcamRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        return () => {
            if (webcamRef.current && webcamRef.current.srcObject) {
                const stream = webcamRef.current.srcObject as MediaStream;
                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
                // Remove stream from video element
                webcamRef.current.srcObject = null;
            }
        };
    }, []);

    let geoLocationOptions = {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 0,
    };

    useEffect(() => {
        if (user.profile) {
            setEmployee(user.profile.profile);
            initPage();
        }
    }, [user]);

    const initPage = async () => {
        await loadFaceModels();
        startVideo();
    }

    const startVideo = () => {
        navigator.mediaDevices
            .getUserMedia({ video: { width: 300 } })
            .then(stream => {
                let video = webcamRef.current as unknown as HTMLVideoElement;
                if (video) {
                    video.srcObject = stream;
                    video.play();

                    if (canvasRef.current) {
                        canvasRef.current.height = video.clientHeight;
                        canvasRef.current.width = video.clientWidth;
                    }
                }
            })
            .catch(err => {
                console.error("error:", err);
            });
    }

    const loadFaceModels = async () => {
        const MODEL_URL = process.env.PUBLIC_URL + '/models';
        await Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
            faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
            faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL)
        ]);
        setIsLoading(false);
    }

    const handleVideoOnPlay = () => {
        setCaptureVideo(true);
    }

    let displaySize = {
        width: 0,
        height: 0,
    }

    useEffect(() => {
        if (!captureVideo) return;

        if (!webcamRef.current) {
            console.log("Webcam not ready");
            return;
        }

        displaySize = {
            width: (webcamRef.current) ? webcamRef.current.clientWidth : 0,
            height: (webcamRef.current) ? webcamRef.current.clientHeight : 0,
        }

        // Create a canvas element for face-api.js on top of the video
        if (canvasRef && canvasRef.current) {
            const canvas = faceapi.createCanvasFromMedia(webcamRef.current);
            if (canvas && canvasRef.current) {
                canvasRef.current.appendChild(canvas);
            }
        }

        runDetectionLoop(); // Start the loop
    }, [captureVideo]);

    const runDetectionLoop = async () => {
        await detectFace();
        if (webcamRef.current) {
            setTimeout(runDetectionLoop, 1000); // Wait 1000ms after detectFace finishes
        }
    }

    const wait = (ms) => {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    const detectFace = async () => {
        if (!webcamRef.current) {
            console.log("Detection loop started, but webcam not ready");
            return;
        }
        if (canvasRef.current) {
            const canvas: HTMLCanvasElement = canvasRef.current;
            const context: CanvasRenderingContext2D = canvas.getContext('2d') as CanvasRenderingContext2D;
            if (context) {
                context.clearRect(0, 0, displaySize.width, displaySize.height);
            }
        }

        const options = new faceapi.TinyFaceDetectorOptions({ scoreThreshold: 0.6 });
        const detection = await faceapi.detectSingleFace(webcamRef.current, options).withFaceLandmarks().withFaceDescriptor();
        if (!detection) {
            setFaceRecognitionStatus("No Face Detected");
            setIsFaceDetected(false);
            return;
        }

        const resizedDetection = faceapi.resizeResults(detection, displaySize);
        const box = resizedDetection.detection.box;
        const drawBox = new DrawBox(box, { label: '', boxColor: 'green', lineWidth: 1 });
        const ctx = canvasRef?.current?.getContext('2d');
        if (ctx) {
            drawBox.draw(ctx);
        }

        setCurrentFaceDescriptor(detection.descriptor);
        setIsFaceDetected(true);
    }

    const registerFace = async () => {
        if (!isFaceDetected) return;

        // Assume you set detected descriptor in `currentFaceDescriptor`
        setFaceDescriptors(descriptors =>
            descriptors.map((desc, idx) =>
                idx === currentSlot ? (currentFaceDescriptor ?? null) : desc
            )
        );
        setFaceRecognitionStatus(`Face ${currentSlot + 1} registered.`);
        setIsFaceDetected(false);
        setCurrentSlot((prev) => (prev + 1) % 3);
    }

    const handleReRegister = (idx) => {
        setFaceDescriptors(descriptors =>
            descriptors.map((desc, idx) =>
                idx === currentSlot ? null : desc
            )
        );
        setCurrentSlot(idx);        
        setFaceRecognitionStatus(`Ready for re-registration for slot ${idx + 1}`);
        setIsFaceDetected(false); // Reset for new capture
    };

    const saveEmployeeFaceDetails = async () => {
        if (!allRegistered) return;
        if (!employee) return;

        const float32Arrays = faceDescriptors
            .filter((desc): desc is Float32Array => desc !== null)
            .map(desc => Array.from(desc));

        const faceData = JSON.stringify(float32Arrays);

        try {
            await ServerGateway.saveEmployeeFaceDescriptor(employee.id, faceData);
            toast.success("Face details saved successfully.");

            wait(5000);
            navigate("/attendance");
        } catch (error) {
            console.error("Error saving face details:", error);
            toast.error("Failed to save face details.");
        }
    };

    const allRegistered = faceDescriptors.every(desc => desc);

    return (
        <div className="page-register-face">
            {isLoading &&
                <Loader />
            }
            {!isLoading &&
                <>
                    <div><Toaster /></div>
                    <div className='row mb-2'>
                        <div className='col-md-12'>
                                <div className='card my-2 p-0'>
                                    <div className='card-header bg-success text-white text-center'>
                                        <h3 className='m-0'>{ employee?.name }</h3>
                                    </div>
                                    <div className='card-body p-0'>
                                        <div className='card-img rounded-0 camera-container'>
                                            <video ref={webcamRef} onLoadedData={handleVideoOnPlay} className='camera-video' />
                                            <canvas ref={canvasRef} className='camera-overlay' />
                                        </div>
                                        <div className='card-text'>
                                            <button type="button" className='btn bg-success text-white w-100 rounded-0' onClick={registerFace} disabled={!isFaceDetected}>
                                                Confirm Image
                                            </button>
                                        </div>
                                        <div className="progress rounded-0">
                                            <div
                                            className="progress-bar"
                                            role="progressbar"
                                            style={{ width: `${faceDescriptors.filter(d => d).length * 33.33}%` }}
                                            aria-valuenow={faceDescriptors.filter(d => d).length * 33.33}
                                            aria-valuemin={0}
                                            aria-valuemax={100}
                                            >                                            
                                                {`${faceDescriptors.filter(d => d).length}/3 faces`}
                                            </div>
                                        </div>
                                        <div className="d-flex flex-row justify-content-between">
                                            {faceDescriptors.map((desc, idx) => (
                                                <div key={idx} className="text-center" style={{ flex: 1 }}>
                                                    <div className={`card alert ${desc ? "alert-success" : "alert-secondary"} rounded-0 p-0 m-0`}>
                                                        <div className='card-body'>
                                                            <strong>Face {idx + 1}</strong><br />
                                                            {desc ? <FontAwesomeIcon icon={faCheck} color={'green'} size={"2x"} /> : <FontAwesomeIcon icon={faTimes} color={'red'} size={"2x"} />}
                                                        </div>
                                                        <div className='card-footer p-0'>
                                                            {desc && (
                                                                <div
                                                                    className="btn btn-warning btn-sm w-100"
                                                                    onClick={() => handleReRegister(idx)}
                                                                >
                                                                    Re-register
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className='card-footer text-center p-0'>
                                        <button type="button" className='btn btn-lg bg-success text-white w-100' onClick={saveEmployeeFaceDetails} disabled={!allRegistered}>
                                            Register Employee
                                        </button>
                                    </div>
                                </div>
                        </div>
                    </div>
                </>
            }
        </div>
    )
};